import { PropTypes } from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ClearColumnFilter from '@ge/components/table/filters/clear-column-filter';
import { EntityType, PresentationTypes, AnaylzeDefs } from '@ge/models/constants';
import { ColumnSelectMenu } from '@ge/shared/components/column-select-menu';
import { ManualAdjustmentsColumnDefs } from '@ge/shared/models/table-col-defs';
import { SitesColumnDefs } from '@ge/shared/models/table-col-defs/sites-list-table-cols';
import PresentationToggle from '@ge/web-client/src/app/components/presentation-toggle/presentation-toggle';

import { usePresentationType } from '../../data-hooks/use-presentation-type';
import { AssetsColumnDefs } from '../../models/assets-table-cols';

import { DownloadTableButton } from './download-table-button';
/**
 * Set list of view types for Presentation Toggle
 */
// const PresentationViews = [PresentationTypes.LIST, PresentationTypes.GRID];
const PresentationViews = [PresentationTypes.GRID, PresentationTypes.LIST];

const StyledTools = styled.div`
  display: flex;
`;

const StyledDownloadTableButton = styled(DownloadTableButton)`
  margin-right: 16px;
  padding: 0;
`;

const ClearButton = styled(ClearColumnFilter)`
  margin-right: 16px;
`;

export const EntityTableTools = ({
  children,
  isLoading,
  data,
  columnState,
  entity,
  entityType,
  dateRange,
  disabledDownload,
  disabledViewToggle,
  className,
  onMenuClose,
  onColumnSelection,
  filters,
  onReset,
  anaylzeDefs,
  ColumnsDefs,
  isDynamicColumns,
  columnsObject,
}) => {
  const { ready, t } = useTranslation(['analyze.dashboard'], {
    useSuspense: false,
  });

  const presentationType = usePresentationType(!disabledViewToggle);

  const columnDef = useMemo(() => {
    switch (entityType) {
      case EntityType.REGION:
      case EntityType.SITES:
        return SitesColumnDefs;
      case EntityType.SITE:
        if (anaylzeDefs === AnaylzeDefs.ASSETS) {
          return AssetsColumnDefs;
        } else if (
          [AnaylzeDefs.CONTRACT_10_MIN_DATA, AnaylzeDefs.COUNTER_SUMMARY].includes(anaylzeDefs)
        ) {
          return ColumnsDefs;
        } else if (anaylzeDefs === AnaylzeDefs.MANUAL_ADJUSTMENTS) {
          return ManualAdjustmentsColumnDefs;
        } else {
          return AssetsColumnDefs;
        }
      default:
        return {};
    }
  }, [anaylzeDefs, ColumnsDefs, entityType]);

  if (!ready) return null;
  return (
    <StyledTools className={className}>
      {children}
      <ClearButton filters={filters} onReset={onReset} />
      {presentationType === PresentationTypes.LIST &&
        (!isDynamicColumns || (isDynamicColumns && Object.keys(columnDef || {}).length > 0)) && (
          <ColumnSelectMenu
            translateFn={t}
            columnDef={columnDef}
            columnState={columnState}
            onMenuClose={onMenuClose}
            heightOffset={690}
            onClumnSelection={onColumnSelection}
          />
        )}
      {!disabledDownload && (
        <StyledDownloadTableButton
          isLoading={isLoading}
          columnDef={columnDef}
          data={data}
          columns={columnState}
          entity={entity}
          entityType={entityType}
          dateRange={dateRange}
          anaylzeDefs={anaylzeDefs}
          columnsObject={columnsObject}
        />
      )}
      {!disabledViewToggle && (
        <PresentationToggle viewList={PresentationViews} defaultView={PresentationTypes.LIST} />
      )}
    </StyledTools>
  );
};

EntityTableTools.propTypes = {
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  columnState: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  data: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  entity: PropTypes.instanceOf(Object),
  entityType: PropTypes.string,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  disabledDownload: PropTypes.bool,
  isDynamicColumns: PropTypes.bool,
  disabledViewToggle: PropTypes.bool,
  className: PropTypes.string,
  onMenuClose: PropTypes.func,
  onColumnSelection: PropTypes.func,
  onReset: PropTypes.func,
  filters: PropTypes.object,
  anaylzeDefs: PropTypes.string,
  ColumnsDefs: PropTypes.Object,
  columnsObject: PropTypes.Object,
};

EntityTableTools.defaultProps = {
  children: null,
  isLoading: false,
  columnState: [],
  data: [],
  entity: null,
  entityType: null,
  dateRange: {},
  disabledDownload: false,
  disabledViewToggle: false,
  className: null,
  onMenuClose: () => {},
  filters: {},
  onReset: () => {},
  anaylzeDefs: null,
  ColumnsDefs: {},
  isDynamicColumns: false,
  columnsObject: {},
};

export default EntityTableTools;
